import axios from 'axios';
import { getApiBaseUrl } from './api';

// -- types -------------------------------------------------------------------

export interface WhatsAppChannel {
  id: string;
  client_id: string;
  channel_type: 'whatsapp';
  provider: 'meta_cloud';
  status: 'pending' | 'active' | 'paused' | 'error';
  display_name: string | null;
  config: { phone_number_id?: string; waba_id?: string; api_version?: string };
  access_mode: 'private' | 'public';
  not_enrolled_reply: string | null;
  managed: boolean;
  last_inbound_at: string | null;
  last_outbound_at: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

export interface WhatsAppRecipient {
  id: string;
  client_id: string;
  channel_id: string;
  phone_e164: string;
  display_name: string | null;
  tags: string[];
  external_user_id: string | null;
  opt_in_status: 'pending' | 'opted_in' | 'opted_out';
  opt_in_method: string | null;
  opt_in_at: string | null;
  opt_out_at: string | null;
  metadata: Record<string, unknown>;
  last_inbound_at: string | null;
  timezone?: string | null;
  preferred_time_local?: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateChannelInput {
  display_name?: string;
  // Optional — when omitted, server auto-fills from META_PHONE_NUMBER_ID +
  // META_WABA_ID env vars (the platform's single Meta App / WABA / number).
  phone_number_id?: string;
  waba_id?: string;
  access_mode?: 'private' | 'public';
  not_enrolled_reply?: string;
  status?: 'pending' | 'active' | 'paused' | 'error';
}

export interface UpdateChannelInput {
  display_name?: string;
  status?: 'pending' | 'active' | 'paused' | 'error';
  access_mode?: 'private' | 'public';
  not_enrolled_reply?: string;
  phone_number_id?: string;
  waba_id?: string;
}

export interface CreateRecipientInput {
  phone_e164: string;
  display_name?: string;
  tags?: string[];
  external_user_id?: string;
  opt_in_confirmed: boolean;
  metadata?: Record<string, unknown>;
  timezone?: string;
  preferred_time_local?: string;
}

export interface UpdateRecipientInput {
  display_name?: string;
  tags?: string[];
  external_user_id?: string;
  metadata?: Record<string, unknown>;
  opt_in_status?: 'pending' | 'opted_in' | 'opted_out';
  timezone?: string;
  preferred_time_local?: string;
}

export interface ListRecipientsParams {
  channel_id?: string;
  status?: 'pending' | 'opted_in' | 'opted_out';
  search?: string;
  limit?: number;
  offset?: number;
}

export interface ListRecipientsResponse {
  recipients: WhatsAppRecipient[];
  total: number;
  limit: number;
  offset: number;
}

// -- API ---------------------------------------------------------------------

// getApiBaseUrl() already includes the `/api` suffix, so we only append the rest.
const base = () => `${getApiBaseUrl()}/clients`;

export const whatsappApi = {
  // -- channels ------------------------------------------------------------

  async createChannel(clientId: string, input: CreateChannelInput): Promise<WhatsAppChannel> {
    const res = await axios.post<{ channel: WhatsAppChannel }>(
      `${base()}/${clientId}/channels`,
      { ...input, channel_type: 'whatsapp', provider: 'meta_cloud' },
    );
    return res.data.channel;
  },

  async listChannels(clientId: string): Promise<WhatsAppChannel[]> {
    const res = await axios.get<{ channels: WhatsAppChannel[] }>(`${base()}/${clientId}/channels`);
    return res.data.channels;
  },

  async updateChannel(
    clientId: string,
    channelId: string,
    input: UpdateChannelInput,
  ): Promise<WhatsAppChannel> {
    const res = await axios.patch<{ channel: WhatsAppChannel }>(
      `${base()}/${clientId}/channels/${channelId}`,
      input,
    );
    return res.data.channel;
  },

  async testSend(
    clientId: string,
    channelId: string,
    toPhoneE164: string,
    options: {
      body?: string;
      template_name?: string;
      template_language?: string;
      template_body_params?: string[];   // strings of form "name=value"
    } = {},
  ): Promise<{ ok: boolean; error?: string; provider_message_id?: string }> {
    const payload: Record<string, unknown> = { to_phone_e164: toPhoneE164 };
    if (options.body) payload.body = options.body;
    if (options.template_name) payload.template_name = options.template_name;
    if (options.template_language) payload.template_language = options.template_language;
    if (options.template_body_params && options.template_body_params.length > 0) {
      payload.template_body_params = options.template_body_params;
    }
    const res = await axios.post(`${base()}/${clientId}/channels/${channelId}/test`, payload);
    return res.data;
  },

  // -- recipients ----------------------------------------------------------

  async listRecipients(
    clientId: string,
    params: ListRecipientsParams = {},
  ): Promise<ListRecipientsResponse> {
    const res = await axios.get<ListRecipientsResponse>(`${base()}/${clientId}/recipients`, {
      params,
    });
    return res.data;
  },

  async createRecipient(
    clientId: string,
    channelId: string,
    input: CreateRecipientInput,
  ): Promise<WhatsAppRecipient> {
    const res = await axios.post<{ recipient: WhatsAppRecipient }>(
      `${base()}/${clientId}/recipients`,
      input,
      { params: { channel_id: channelId } },
    );
    return res.data.recipient;
  },

  async updateRecipient(
    clientId: string,
    recipientId: string,
    input: UpdateRecipientInput,
  ): Promise<WhatsAppRecipient> {
    const res = await axios.patch<{ recipient: WhatsAppRecipient }>(
      `${base()}/${clientId}/recipients/${recipientId}`,
      input,
    );
    return res.data.recipient;
  },

  async optOutRecipient(clientId: string, recipientId: string): Promise<WhatsAppRecipient> {
    const res = await axios.post<{ recipient: WhatsAppRecipient }>(
      `${base()}/${clientId}/recipients/${recipientId}/opt-out`,
    );
    return res.data.recipient;
  },

  // -- simulator (real backend, writes observations) ----------------------
  // The simulator runs the actual workflow engine in `simulator_mode=true` —
  // outbound bubbles land in `simulator_messages` instead of going to Meta,
  // but inbound replies still flow through engine.handle_inbound() so
  // observations persist to recipient_observations exactly as in production.

  async simulatorStart(
    clientId: string,
    workflowId: string,
    recipientId: string,
  ): Promise<{ session: SimulatorSession; thread: SimulatorMessage[] }> {
    const res = await axios.post(
      `${base()}/${clientId}/workflows/${workflowId}/simulate-for/${recipientId}`,
      {},
    );
    return res.data;
  },

  async simulatorThread(
    clientId: string,
    recipientId: string,
    sessionId?: string,
  ): Promise<{ thread: SimulatorMessage[]; active_session: SimulatorSession | null }> {
    const params = sessionId ? `?session_id=${sessionId}` : '';
    const res = await axios.get(
      `${base()}/${clientId}/recipients/${recipientId}/simulator-thread${params}`,
    );
    return res.data;
  },

  async simulatorInbound(
    clientId: string,
    recipientId: string,
    text: string,
    sessionId?: string,
  ): Promise<{
    handled: boolean;
    session?: SimulatorSession;
    thread?: SimulatorMessage[];
    session_id?: string;
  }> {
    const res = await axios.post(
      `${base()}/${clientId}/recipients/${recipientId}/simulator-inbound`,
      { text, session_id: sessionId },
    );
    return res.data;
  },

  async simulatorReset(
    clientId: string,
    recipientId: string,
    purgeThread = true,
  ): Promise<{ closed: number; purged: number }> {
    const res = await axios.post(
      `${base()}/${clientId}/recipients/${recipientId}/simulator-reset?purge_thread=${purgeThread}`,
    );
    return res.data;
  },
};

// -- simulator types ---------------------------------------------------------

export interface SimulatorChoice {
  label: string;
  image_url?: string;
}

export interface SimulatorMessage {
  id: string;
  session_id: string | null;
  step_id: string | null;
  direction: 'outbound' | 'inbound';
  body: string | null;
  image_url: string | null;
  /** Set on outbound bubbles when the engine is awaiting a reply of this shape. */
  response_type: 'text' | 'number' | 'choice' | 'scale' | 'emoji' | null;
  /** Backend shapes choices as objects so per-choice images can ride along. */
  choices: SimulatorChoice[] | null;
  created_at: string | null;
}

export interface SimulatorSession {
  id: string;
  workflow_id?: string;
  current_step_id?: string | null;
  status?: string;
  simulator_mode?: boolean;
  started_at?: string | null;
}
